import React, { useState, useEffect } from 'react';
import { Plus, Store } from 'lucide-react';
import { apiCall } from 'core/api/apiService';
import { useTheme } from '../../../core/ui/ThemeContext';
import { useLanguage } from '../../../core/i18n/LanguageContext';
import InternalShopForm from './InternalShopForm';
import InternalShopList from './InternalShopList';
import DeleteConfirmationModal from './DeleteConfirmationModal';

const InternalShopManagement = ({ user, onManageProducts, onShopsCountUpdate, showSuccess, showError, showWarning }) => {
  const { isDarkMode } = useTheme();
  const { t } = useLanguage();
  const [internalShops, setInternalShops] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingShop, setEditingShop] = useState(null);
  const [showShopForm, setShowShopForm] = useState(false);
  const [deleteConfirmation, setDeleteConfirmation] = useState(null);

  useEffect(() => {
    loadInternalShops();
  }, []);

  const loadInternalShops = async () => {
    try {
      setLoading(true);
      const response = await apiCall('/internal-shops', 'GET', null, user);
      const shops = response || [];
      setInternalShops(shops);
      
      // Anzahl an AdminView weiterleiten
      if (onShopsCountUpdate) {
        onShopsCountUpdate(shops.length);
      }
    } catch (error) {
      console.error('Failed to load internal shops:', error);
      if (showError) {
        showError(t('admin.internalShops.loadError'));
      } else {
        alert(t('admin.internalShops.loadError'));
      }
      // Auch bei Fehler die Anzahl auf 0 setzen
      if (onShopsCountUpdate) {
        onShopsCountUpdate(0);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSaveShop = async (shopData) => {
    try {
      const method = editingShop ? 'PUT' : 'POST';
      const endpoint = editingShop ? `/internal-shops/${editingShop.id}` : '/internal-shops';
      
      await apiCall(endpoint, method, shopData, user);
      
      if (showSuccess) {
        showSuccess(editingShop ? t('admin.internalShops.updated') : t('admin.internalShops.saved'));
      } else {
        alert(editingShop ? t('admin.internalShops.updated') : t('admin.internalShops.saved'));
      }
      setEditingShop(null);
      setShowShopForm(false);
      await loadInternalShops();
    } catch (error) {
      console.error('Failed to save internal shop:', error);
      if (showError) {
        showError(t('admin.internalShops.saveError', { error: error.message }));
      } else {
        alert(t('admin.internalShops.saveError', { error: error.message }));
      }
    }
  };

  const handleDeleteShop = async (shopId) => {
    try {
      await apiCall(`/internal-shops/${shopId}`, 'DELETE', null, user);
      if (showSuccess) {
        showSuccess(t('admin.internalShops.deleted'));
      } else {
        alert(t('admin.internalShops.deleted'));
      }
      setDeleteConfirmation(null);
      await loadInternalShops();
    } catch (error) {
      console.error('Failed to delete internal shop:', error);
      if (showError) {
        showError(t('admin.internalShops.deleteError'));
      } else {
        alert(t('admin.internalShops.deleteError'));
      }
    }
  };

  const handleEditShop = (shop) => {
    setEditingShop(shop);
    setShowShopForm(true);
  };

  const handleAddShop = () => {
    setEditingShop(null);
    setShowShopForm(true);
  };

  if (showShopForm) {
    return (
      <InternalShopForm
        shop={editingShop}
        onSave={handleSaveShop}
        onCancel={() => {
          setEditingShop(null);
          setShowShopForm(false);
        }}
        showWarning={showWarning}
      />
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className={`relative group ${isDarkMode ? '' : 'glass-light-card'} rounded-3xl`}>
        {/* Glass Background */}
        <div className={`absolute inset-0 ${isDarkMode
          ? 'bg-gradient-to-br from-gray-900/30 via-gray-800/20 to-gray-900/30 backdrop-blur-2xl border border-white/20 shadow-[0_8px_32px_rgba(0,0,0,0.4)]'
          : ''
        } rounded-3xl`}></div>
        <div className={`absolute inset-0 ${isDarkMode
          ? 'bg-gradient-to-t from-white/5 via-transparent to-white/10'
          : ''
        } rounded-3xl`}></div>

        <div className="relative p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="relative group/icon">
                <div className={`absolute inset-0 ${isDarkMode
                  ? 'bg-gradient-to-br from-white/20 via-white/10 to-transparent'
                  : 'hidden'
                } rounded-2xl blur-sm`}></div>
                <div className={`relative ${isDarkMode
                  ? 'bg-gradient-to-br from-blue-600/60 via-blue-700/50 to-blue-800/60 backdrop-blur-xl shadow-[0_8px_32px_rgba(59,130,246,0.3),inset_0_1px_0_rgba(255,255,255,0.3)] border border-white/20'
                  : 'bg-brand/10'
                } w-12 h-12 rounded-2xl flex items-center justify-center`}>
                  <Store className={`w-6 h-6 ${isDarkMode ? 'text-white/90' : 'text-brand'}`} />
                  <div className={`absolute top-1 left-1 w-3 h-3 bg-white/30 rounded-full blur-sm ${isDarkMode ? '' : 'hidden'}`}></div>
                </div>
              </div>
              <div>
                <h2 className={`text-xl font-bold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>{t('admin.internalShops.title')}</h2>
                <p className={`${isDarkMode ? 'text-white/70' : 'text-slate-600'}`}>{t('admin.internalShops.subtitle')}</p>
              </div>
            </div>

            {/* Primär-Button */}
            <button
              onClick={handleAddShop}
              className="relative group/button overflow-hidden rounded-2xl transition-all duration-300 transform hover:scale-[1.01] active:scale-[0.99]"
              style={{
                background: isDarkMode
                  ? 'linear-gradient(135deg, rgba(59,130,246,0.7) 0%, rgba(37,99,235,0.7) 50%, rgba(29,78,216,0.7) 100%)'
                  : '#2563EB',
                backdropFilter: isDarkMode ? 'blur(20px)' : 'none',
                border: isDarkMode ? '1px solid rgba(255,255,255,0.3)' : 'none',
                boxShadow: isDarkMode
                  ? '0 8px 32px rgba(59,130,246,0.4), inset 0 1px 0 rgba(255,255,255,0.2)'
                  : 'none'
              }}
            >
              {/* Hover Overlay */}
              <div
                className="absolute inset-0 rounded-2xl transition-all duration-300 opacity-0 group-hover/button:opacity-100"
                style={{
                  background: isDarkMode
                    ? 'linear-gradient(135deg, rgba(255,255,255,0.08) 0%, rgba(255,255,255,0.15) 50%, rgba(255,255,255,0.08) 100%)'
                    : 'rgba(255,255,255,0.12)'
                }}
              ></div>

              <div className="relative z-10 px-4 py-2 flex items-center font-medium text-white transition-all duration-300">
                <Plus className="w-4 h-4 mr-2 transition-transform duration-300 group-hover/button:scale-110" />
                {t('admin.internalShops.newShop')}
              </div>

              {/* Wave Effect (nur Dark) */}
              <div className={`absolute inset-0 opacity-0 group-hover/button:opacity-100 transition-opacity duration-500 ${isDarkMode ? '' : 'hidden'}`}>
                <div
                  className="absolute inset-0 transform -skew-x-12 -translate-x-full group-hover/button:translate-x-full transition-transform duration-800"
                  style={{ background: 'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.12) 50%, transparent 100%)' }}
                ></div>
              </div>
            </button>
          </div>
        </div>
      </div>

      <InternalShopList
        shops={internalShops}
        loading={loading}
        onAddShop={handleAddShop}
        onEditShop={handleEditShop}
        onDeleteShop={setDeleteConfirmation}
        onManageProducts={onManageProducts}
      />

      <DeleteConfirmationModal
        shop={deleteConfirmation}
        onConfirm={handleDeleteShop}
        onCancel={() => setDeleteConfirmation(null)}
      />
    </div>
  );
};

export default InternalShopManagement; 